import { z } from 'zod';

import { ContentType } from '../../enums';

export const CommonToolNames = {
  RagResult: 'rag_result',
  RagResultOperator: 'rag_result_operator',
  UsageLimitsError: 'usageLimitsError',
} as const;

export const ragResultToolDef = z.object({
  content: z
    .string()
    .describe(
      'Main response to the user in the conversation language, formatted in markdown. Must NEVER be empty: when the answer is not found in the documents (or no documents are provided), still write a short, polite message explaining that this information is not available.',
    ),
  sources: z
    .array(
      z.object({
        type: z.nativeEnum(ContentType).describe('Source document type'),
        fileName: z
          .string()
          .describe(
            'Human-readable source label for UI chips and lists. For uploaded documents: prettify metadata file name (remove underscores, capitalize, drop extensions). For website or external URL sources: include hostname and URL path when the page is not the domain root (e.g. example.com/pricing, example.com/cs/about), not only example.com, so different subpages stay distinguishable. Omit scheme (https). Add path segments, query string, and hash when they matter for disambiguation.',
          ),
        url: z.string().describe('Document URL from metadata'),
        page: z
          .string()
          .describe(
            'Number of page in the document on which the information was found. The page number is stored in the metadata. Take the number of page as it is and DO NOT modify it.',
          ),
        header: z
          .string()
          .describe(
            'Legal Journal Reference Number in the exact format "č. {number}/{year} LJ" (e.g. "č. 4/2023 LJ") or "č. {number}/{year} Sb." (e.g. "č. 89/2012 Sb."). Extract only if it matches one of these exact formats.',
          )
          .nullish(),
        article: z
          .string()
          .describe(
            'Article (článek) reference in legal documents in the exact format "čl. {number}" (e.g. "čl. 5"). This represents a specific section of the legal document. Extract only if it matches this exact format.',
          )
          .nullish(),
        note: z
          .string()
          .optional()
          .describe(
            'Short display-only annotation for this source (e.g. "pricing table"), shown on hover. Never a URL or label.',
          ),
        mimeType: z.string().optional(),
        documentId: z
          .string()
          .optional()
          .describe(
            'Source document id — lets the widget merge citations of the same document across different pages into one entry.',
          ),
        id: z
          .string()
          .optional()
          .describe(
            'Inline citation registry id (c1, c2, …) for marker rendering',
          ),
        ids: z
          .array(z.string())
          .optional()
          .describe(
            'All citation ids that resolve to this same source (aliases after dedupe). The widget maps every alias to this source and its number.',
          ),
      }),
    )
    .nullish()
    .describe('List of source documents referenced in the response'),
  answered: z.boolean().describe('Whether the question was answered'),
});

export const ragResultToolOperator = z.object({
  content: z
    .string()
    .describe(
      'natural conversationally response to the user, use the chat history to form the response',
    ),
  operator: z
    .object({
      name: z.string().nullish().describe("Selected operator's name"),
      email: z.string().nullish().describe("Selected operator's email"),
      phone: z.string().nullish().describe("Selected operator's phone"),
      displayText: z
        .string()
        .nullish()
        .describe("Selected operator's displayText"),
    })
    .nullish()
    .describe('Operator contact information'),
});

export type RagResultToolDef = z.infer<typeof ragResultToolDef>;
export type RagResultToolOperator = z.infer<typeof ragResultToolOperator>;

/**
 * Manual "tool", we use this to provide better error messages to the
 * user when limits are exceeded. This tool is not really a LLM tool,
 * but we insert it manually on client side, leveraging the tool registry.
 */
export const usageLimitsErrorToolArgsSchema = z.object({
  operator: z
    .object({
      name: z.string().describe('Operator name').nullable(),
      email: z.string().describe('Operator email').nullable(),
      phone: z.string().describe('Operator phone').nullable(),
    })
    .nullable(),
});

export type UsageLimitsErrorToolArgs = z.infer<
  typeof usageLimitsErrorToolArgsSchema
>;
